import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnterprise } from '../../contexts/EnterpriseContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Clock, AlertTriangle, ChevronDown, ArrowRight, Users } from 'lucide-react';
import { toast } from 'sonner';

const DENIAL_COLORS = {
  'Medical necessity': '#ef4444',
  'Coding error': '#f59e0b',
  'Authorization': '#f97316',
  'Timely filing': '#ef4444',
  'Duplicate': '#9ca3af',
  'Administrative': '#6b7280',
};

const REASON_COLORS = {
  '> 30 days unworked': '#f59e0b',
  'High-value denial ($500+)': '#ef4444',
  'Appeal deadline approaching': '#f97316',
  'Repeatedly denied': '#ef4444',
};

const rateColor = (val) => val >= 95 ? '#16a34a' : val >= 85 ? '#d97706' : '#ef4444';
const fmt = (v) => `$${v.toLocaleString()}`;

const RevQSupervisorDashboard = () => {
  const navigate = useNavigate();
  const { kpi, teamPerf, providerPerf, denialTrends, attentionClaims, teamMembers, isEnterprise, currentRole } = useEnterprise();
  const [dateRange, setDateRange] = useState('month');
  const [assignDropdown, setAssignDropdown] = useState(null);

  const billers = teamMembers.filter(m => m.role === 'biller' || m.role === 'denials');

  const handleAssign = (claimId, memberName) => {
    setAssignDropdown(null);
    toast.success(`Claim ${claimId} assigned to ${memberName}`);
  };

  if (!isEnterprise || (currentRole !== 'supervisor' && currentRole !== 'admin')) {
    return (
      <div className="space-y-6" data-testid="supervisor-dashboard">
        <div style={{ background: '#f9fafb', borderRadius: 12, padding: 40, textAlign: 'center' }}>
          <Users style={{ width: 32, height: 32, color: '#ccc', margin: '0 auto 12px' }} />
          <p style={{ fontSize: 14, color: '#888' }}>Supervisor dashboard requires Enterprise mode and Supervisor role</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="supervisor-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Supervisor Dashboard</h1>
          <p className="text-slate-500 mt-1">Team performance and billing operations overview</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={dateRange} onChange={e => setDateRange(e.target.value)}
            style={{ height: 34, padding: '0 12px', border: '0.5px solid #e0e0e0', borderRadius: 7, fontSize: 12, color: '#555', background: '#fff', cursor: 'pointer' }}
            data-testid="date-range-filter">
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="30days">Last 30 days</option>
          </select>
          <button onClick={() => navigate('/revq')}
            style={{ height: 34, padding: '0 14px', border: '0.5px solid #e0e0e0', borderRadius: 7, fontSize: 12, color: '#555', background: '#fff', cursor: 'pointer' }}
            data-testid="exit-supervisor-btn">
            Exit Billing Focus
          </button>
        </div>
      </div>

      {/* KPI Summary Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }} data-testid="kpi-summary">
        <KPICard label="First-pass rate" value={`${kpi.firstPassRate}%`}
          trend={kpi.firstPassTrend > 0 ? `+${kpi.firstPassTrend}%` : `${kpi.firstPassTrend}%`}
          trendUp={kpi.firstPassTrend > 0} />
        <KPICard label="Days in A/R" value={kpi.daysInAR}
          sub={`Target: <${kpi.arTarget} days`}
          indicator={kpi.daysInAR <= kpi.arTarget ? '#16a34a' : '#d97706'} />
        <KPICard label="Denial rate" value={`${kpi.denialRate}%`}
          trend={kpi.denialTrend > 0 ? `+${kpi.denialTrend}%` : `${kpi.denialTrend}%`}
          trendUp={kpi.denialTrend > 0}
          sub={`Industry avg: ${kpi.industryAvg}%`} />
        <KPICard label="Open claims" value={kpi.openClaims}
          sub={kpi.pendingOver30 > 0 ? `${kpi.pendingOver30} pending >30 days` : null}
          subColor={kpi.pendingOver30 > 0 ? '#d97706' : null} />
      </div>

      {/* Team Performance Table */}
      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden' }} data-testid="team-performance">
        <div style={{ padding: '14px 16px', borderBottom: '0.5px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111', margin: 0 }}>Team performance</h3>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '0.5px solid #f0f0f0' }}>
              {['Name', 'Role', 'Claims processed', 'First-pass rate', 'Avg. time', 'Denial rate', 'Open items'].map(h => (
                <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teamPerf.map(m => (
              <tr key={m.name} style={{ borderBottom: '0.5px solid #f9f9f9', cursor: 'pointer' }}
                onClick={() => toast.info(`${m.name} claim log coming soon`)}
                data-testid={`team-row-${m.name.replace(/\s/g, '-').toLowerCase()}`}>
                <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 500, color: '#111' }}>{m.name}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#888' }}>{m.role}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#111' }}>{m.claimsProcessed}</td>
                <td style={{ padding: '10px 14px' }}>
                  {m.firstPass !== null ? (
                    <span style={{ fontSize: 13, fontWeight: 500, color: rateColor(m.firstPass) }}>{m.firstPass}%</span>
                  ) : <span style={{ fontSize: 11, color: '#aaa' }}>N/A</span>}
                </td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#555' }}>{m.avgTime} min</td>
                <td style={{ padding: '10px 14px' }}>
                  {m.denialRate !== null ? (
                    <span style={{ fontSize: 13, color: m.denialRate > 5 ? '#ef4444' : '#555' }}>{m.denialRate}%</span>
                  ) : <span style={{ fontSize: 11, color: '#aaa' }}>N/A</span>}
                </td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: m.openItems > 10 ? '#d97706' : '#111' }}>{m.openItems}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Provider Performance Table */}
      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden' }} data-testid="provider-performance">
        <div style={{ padding: '14px 16px', borderBottom: '0.5px solid #f0f0f0' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111', margin: 0 }}>Provider performance</h3>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '0.5px solid #f0f0f0' }}>
              {['Provider', 'Specialty', 'Claims submitted', 'Denial rate', 'Top denial reason', 'Revenue', 'Est. pending'].map(h => (
                <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {providerPerf.map(p => (
              <tr key={p.name} style={{ borderBottom: '0.5px solid #f9f9f9' }} data-testid={`provider-row-${p.name.replace(/\s/g, '-').toLowerCase()}`}>
                <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 500, color: '#111' }}>{p.name}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#888' }}>{p.specialty}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#111' }}>{p.claims}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ fontSize: 13, color: p.denialRate > 5 ? '#ef4444' : '#555' }}>{p.denialRate}%</span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    fontSize: 10, padding: '2px 8px', borderRadius: 10,
                    background: p.topDenial === 'Medical necessity' ? '#fef2f2' : p.topDenial === 'Missing modifier' ? '#fffbeb' : '#fff7ed',
                    color: p.topDenial === 'Medical necessity' ? '#ef4444' : p.topDenial === 'Missing modifier' ? '#d97706' : '#f97316',
                    fontWeight: 500,
                  }}>{p.topDenial}</span>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#111' }}>{fmt(p.revenue)}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#d97706' }}>{fmt(p.pending)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Denial Trends Chart + Claims Needing Attention */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Denial Trends */}
        <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, padding: 16 }} data-testid="denial-trends">
          <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111', margin: '0 0 14px' }}>Denial trends</h3>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={denialTrends} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 10, fill: '#aaa' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="category" tick={{ fontSize: 10, fill: '#555' }} width={120} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {denialTrends.map((entry) => (
                    <Cell key={entry.category} fill={DENIAL_COLORS[entry.category] || '#6b7280'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p style={{ fontSize: 11, color: '#888', marginTop: 12, lineHeight: 1.5 }}>
            Top action this week: 7 of 12 denials this month were medical necessity denials for 99214. Consider a documentation refresher for providers billing E/M Level 4.
          </p>
        </div>

        {/* Claims Needing Attention */}
        <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, padding: 16 }} data-testid="claims-attention">
          <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111', margin: '0 0 14px' }}>Claims needing attention</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {attentionClaims.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#f9fafb', borderRadius: 8 }}
                data-testid={`attention-claim-${c.id}`}>
                <AlertTriangle style={{ width: 12, height: 12, color: REASON_COLORS[c.reason] || '#d97706', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, fontWeight: 500, color: '#111', margin: 0 }}>{c.id} &middot; {c.patient}</p>
                  <p style={{ fontSize: 10, color: '#aaa', margin: '1px 0 0' }}>{c.provider} &middot; {c.reason} &middot; {c.days}d</p>
                </div>
                <span style={{ fontSize: 12, fontWeight: 500, color: '#111', flexShrink: 0 }}>{fmt(c.amount)}</span>
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setAssignDropdown(assignDropdown === c.id ? null : c.id)}
                    style={{
                      fontSize: 10, padding: '3px 8px', border: '0.5px solid #e0e0e0', borderRadius: 5,
                      background: '#fff', cursor: 'pointer', color: c.assignedTo ? '#555' : '#d97706', whiteSpace: 'nowrap',
                    }}
                    data-testid={`assign-btn-${c.id}`}>
                    {c.assignedTo || 'Assign to'} <ChevronDown style={{ width: 8, height: 8, display: 'inline' }} />
                  </button>
                  {assignDropdown === c.id && (
                    <div style={{
                      position: 'absolute', right: 0, top: '100%', marginTop: 4, width: 180, background: '#fff',
                      border: '1px solid #e0e0e0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 20,
                    }}>
                      {billers.map(b => (
                        <button key={b.id} onClick={() => handleAssign(c.id, b.name)}
                          style={{ display: 'block', width: '100%', padding: '6px 12px', border: 'none', background: '#fff', cursor: 'pointer', textAlign: 'left', fontSize: 12, color: '#333' }}>
                          {b.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── KPI Card ── */
const KPICard = ({ label, value, trend, trendUp, sub, subColor, indicator }) => (
  <div style={{ background: '#f9fafb', borderRadius: 10, padding: 14 }} data-testid={`kpi-${label.replace(/\s/g, '-').toLowerCase()}`}>
    <p style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>{label}</p>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
      <span style={{ fontSize: 28, fontWeight: 700, color: '#111', lineHeight: 1 }}>{value}</span>
      {trend && (
        <span style={{ fontSize: 11, fontWeight: 500, color: trendUp ? '#16a34a' : '#ef4444', display: 'flex', alignItems: 'center', gap: 2 }}>
          {trendUp ? <TrendingUp style={{ width: 10, height: 10 }} /> : <TrendingDown style={{ width: 10, height: 10 }} />}
          {trend}
        </span>
      )}
      {indicator && <span style={{ width: 8, height: 8, borderRadius: '50%', background: indicator }} />}
    </div>
    {sub && <p style={{ fontSize: 10, color: subColor || '#aaa', margin: '4px 0 0' }}>{sub}</p>}
  </div>
);

export default RevQSupervisorDashboard;
